import { useCallback, useState } from 'react';
import { Res } from 'api/api.types';
import { axiosInstance } from 'api/axios';
import {
  EditCorrectiveInvoiceBody,
  EditInvoiceBody,
} from 'api/invoices/invoices.types';
import {
  prepareRemoveCorrectiveInvoiceLinePayload,
  prepareRemoveInvoiceLinePayload,
} from 'pages/invoices/components/InvoiceDetails/components/InvoiceLinesPanel/components/DeleteInvoiceLineDialog/utils';
import {
  CorrectiveInvoice,
  Invoice,
  InvoiceLine,
} from 'pages/invoices/invoices.types';
import { isReverted } from 'utils/invoiceFunctions.utils';

export const useRemoveInvoiceLine = (): {
  loading: boolean;
  removeInvoiceLine: (
    invoice: Invoice,
    invoiceLine: InvoiceLine,
  ) => Promise<undefined | Res<Invoice>>;
} => {
  const [isLoading, setIsLoading] = useState(false);

  const removeInvoiceLine = useCallback(
    (invoice: Invoice, invoiceLine: InvoiceLine) => {
      setIsLoading(true);
      return axiosInstance
        .put<Res<Invoice>>(
          `/invoice/${invoice.id}/removeLine/${invoiceLine.id!}`,
          prepareRemoveInvoiceLinePayload(invoice, invoiceLine),
        )
        .then((removeLineRes) => {
          if (removeLineRes.data.status === 'OK') {
            if (isReverted(invoice)) {
              return axiosInstance
                .delete(`/accountentry/invoice/${invoice.id}/removeAll`)
                .then((removeAllRes) => {
                  if (removeAllRes.data.status === 'OK') {
                    return removeLineRes.data;
                  }

                  throw removeAllRes.data.message; //TODO handle errors
                });
            }

            return removeLineRes.data;
          }
          throw removeLineRes.data.message;
        })
        .catch((e) => {
          console.error(e);
          return undefined;
        })
        .finally(() => setIsLoading(false));
    },
    [],
  );

  return {
    loading: isLoading,
    removeInvoiceLine,
  };
};

export const useRemoveCorrectiveInvoiceLine = (): {
  loading: boolean;
  removeCorrectiveInvoiceLine: (
    invoice: CorrectiveInvoice,
    invoiceLine: InvoiceLine,
    isLinePrevious: boolean,
  ) => Promise<undefined | Res<CorrectiveInvoice>>;
} => {
  const [isLoading, setIsLoading] = useState(false);

  const removeCorrectiveInvoiceLine = useCallback(
    (
      invoice: CorrectiveInvoice,
      invoiceLine: InvoiceLine,
      isLinePrevious: boolean,
    ) => {
      setIsLoading(true);
      return axiosInstance
        .put<Res<CorrectiveInvoice>>(
          `/correctiveinvoice/${invoice.id}`,
          prepareRemoveCorrectiveInvoiceLinePayload(
            invoice,
            invoiceLine,
            isLinePrevious,
          ),
        )
        .then((removeLineRes) => {
          if (removeLineRes.data.status === 'OK') {
            if (isReverted(invoice)) {
              return axiosInstance
                .delete(`/accountentry/invoice/${invoice.id}/removeAll`)
                .then((removeAllRes) => {
                  if (removeAllRes.data.status === 'OK') {
                    return removeLineRes.data;
                  }

                  throw removeAllRes.data.message; //TODO handle errors
                });
            }

            return removeLineRes.data;
          }
          throw removeLineRes.data.message;
        })
        .catch((e) => {
          console.error(e);
          return undefined;
        })
        .finally(() => setIsLoading(false));
    },
    [],
  );

  return {
    loading: isLoading,
    removeCorrectiveInvoiceLine,
  };
};

export const useEditInvoice = (): {
  loading: boolean;
  editInvoice: (
    invoice: Invoice,
    editedInvoice: EditInvoiceBody,
  ) => Promise<undefined | Res<Invoice>>;
} => {
  const [isLoading, setIsLoading] = useState(false);

  const editInvoice = useCallback(
    (invoice: Invoice, editedInvoice: EditInvoiceBody) => {
      setIsLoading(true);
      return axiosInstance
        .put<Res<Invoice>>(`/invoice/${invoice.id}`, editedInvoice)
        .then((editInvoiceRes) => {
          if (editInvoiceRes.data.status === 'OK') {
            if (isReverted(invoice)) {
              return axiosInstance
                .delete(`/accountentry/invoice/${invoice.id}/removeAll`)
                .then((removeAllRes) => {
                  if (removeAllRes.data.status === 'OK') {
                    return editInvoiceRes.data;
                  }

                  throw removeAllRes.data.message; //TODO handle errors
                });
            }

            return editInvoiceRes.data;
          }
          throw editInvoiceRes.data.message;
        })
        .catch((e) => {
          console.error(e);
          return undefined;
        })
        .finally(() => setIsLoading(false));
    },
    [],
  );

  return {
    loading: isLoading,
    editInvoice,
  };
};

export const useEditCorrectiveInvoice = (): {
  loading: boolean;
  editCorrectiveInvoice: (
    invoice: Invoice,
    editedCorrectiveInvoice: EditCorrectiveInvoiceBody,
  ) => Promise<undefined | Res<CorrectiveInvoice>>;
} => {
  const [isLoading, setIsLoading] = useState(false);

  const editCorrectiveInvoice = useCallback(
    (invoice: Invoice, editedCorrectiveInvoice: EditCorrectiveInvoiceBody) => {
      setIsLoading(true);
      return axiosInstance
        .put<Res<CorrectiveInvoice>>(
          `/correctiveinvoice/${invoice.id}`,
          editedCorrectiveInvoice,
        )
        .then((editCorrInvoiceRes) => {
          if (editCorrInvoiceRes.data.status === 'OK') {
            if (isReverted(invoice)) {
              return axiosInstance
                .delete(`/accountentry/invoice/${invoice.id}/removeAll`)
                .then((removeAllRes) => {
                  if (removeAllRes.data.status === 'OK') {
                    return editCorrInvoiceRes.data;
                  }

                  throw removeAllRes.data.message; //TODO handle errors
                });
            }

            return editCorrInvoiceRes.data;
          }
          throw editCorrInvoiceRes.data.message;
        })
        .catch((e) => {
          console.error(e);
          return undefined;
        })
        .finally(() => setIsLoading(false));
    },
    [],
  );

  return {
    loading: isLoading,
    editCorrectiveInvoice,
  };
};
